import { Menu, Popover, Transition } from '@headlessui/react';
import clsx from 'clsx';
import { Fragment } from 'react';
import { Link, useLocation } from 'react-router';

import { PROGRAM_SLUGS } from '@/lib/constants';
import { handleFormClick } from '@/lib/functions';
import { Button } from './Button';
import { Icon } from './Icon';

const navigation = [
  { label: 'Нүүр хуудас', href: '/' },
  { label: 'Бидний тухай', href: '/about' },
  { label: 'Тэмцээн, уралдаан', href: '/competitions' },
  { label: 'Мэдээ мэдээлэл', href: '/blogs' },
  { label: 'Хөтөлбөрүүд' },
  { label: 'R U OK ?', href: '/r-u-ok' },
  { label: 'Галлерей', href: '/gallery' },
  { label: 'Холбоо барих', href: '/contact' },
];

export function Navbar({ contact }: { contact: any }) {
  const location = useLocation();
  // A section's own pages count too: /competitions/3 underlines "Тэмцээн, уралдаан".
  const isActive = (href: string) =>
    location.pathname === href || (href !== '/' && location.pathname.startsWith(`${href}/`));

  function MenuIcon({ open }: { open: boolean }) {
    return (
      <>
        <span
          className={clsx(
            'absolute block h-1 rotate-0 transform rounded-full opacity-100 transition-all duration-300 ease-in-out',
            open
              ? 'left-1/2 top-2 w-0 bg-green-50 group-hover:bg-white'
              : 'left-0 top-0 w-full bg-green-900 group-hover:bg-green-600',
          )}
        />
        <span
          className={clsx(
            'absolute left-0 top-2 block h-1 w-full transform rounded-full opacity-100 transition-all duration-300 ease-in-out group-hover:bg-green-600',
            open
              ? 'rotate-45 bg-green-50 group-hover:bg-white'
              : 'rotate-0 bg-green-900 group-hover:bg-green-600',
          )}
        />
        <span
          className={clsx(
            'absolute left-0 top-2 block h-1 w-full transform rounded-full opacity-100 transition-all duration-300 ease-in-out group-hover:bg-green-600',
            open
              ? '-rotate-45 bg-green-50 group-hover:bg-white'
              : 'rotate-0 bg-green-900 group-hover:bg-green-600',
          )}
        />
        <span
          className={clsx(
            'absolute block h-1 rotate-0 transform rounded-full opacity-100 transition-all duration-300 ease-in-out group-hover:bg-green-600',
            open
              ? 'left-1/2 top-2 w-0 bg-green-50 group-hover:bg-white'
              : 'left-0 top-4 w-full bg-green-900 group-hover:bg-green-600',
          )}
        />
      </>
    );
  }

  function MobileNav() {
    return (
      <div className="block lg:hidden">
        <Popover>
          <Popover.Button
            className="group relative z-50 flex h-11 w-11 items-center justify-center rotate-0 transform cursor-pointer transition duration-500 ease-in-out focus:outline-none"
            aria-label="Toggle Navigation"
          >
            {({ open }) => (
              <span className="relative block h-5 w-6">
                <MenuIcon open={open} />
              </span>
            )}
          </Popover.Button>
          <Transition
            as={Fragment}
            enter="duration-300 ease-out"
            enterFrom="opacity-0 -translate-y-full"
            enterTo="opacity-100 translate-y-0"
            leave="duration-200 ease-in"
            leaveFrom="opacity-100 translate-y-0"
            leaveTo="opacity-0 -translate-y-full"
          >
            <Popover.Panel
              as="div"
              className="absolute inset-x-0 top-0 z-40 w-screen overflow-y-scroll bg-gradient-to-tr from-green-600 to-green-600 px-4 py-16 sm:px-8"
            >
              <div className="flex h-full w-full flex-col items-center justify-center">
                <div className="mx-auto flex w-full flex-col items-center justify-evenly space-y-6">
                  {navigation.map((link) => (
                    <Fragment key={`mobile-link-${link.label}`}>
                      {link.label !== 'Хөтөлбөрүүд' && link.href && (
                        <Link to={link.href}>
                          <div className="group relative p-0.5">
                            <span className="relative z-10 text-2xl font-medium text-green-50 duration-300 ease-in-out group-hover:text-white">
                              {link.label}
                            </span>
                            <span className="absolute -left-1 -right-1 bottom-0 h-1.5 origin-bottom scale-x-0 transform rounded-lg bg-yellow-400 duration-300 ease-in-out group-hover:scale-x-100" />
                          </div>
                        </Link>
                      )}
                    </Fragment>
                  ))}
                  <Button onClick={() => handleFormClick(contact.registration)}>Бүртгүүлэх</Button>
                </div>
                <hr className="my-8 w-full border-green-200 border-opacity-30 sm:my-10" />
                <div className="mx-auto w-full max-w-md">
                  <p className="text-center text-lg font-semibold uppercase tracking-wider text-green-200 sm:text-left">
                    Хөтөлбөрүүд
                  </p>
                  <div className="mt-4 grid justify-items-center gap-4 sm:grid-cols-2 sm:justify-items-start sm:gap-x-8">
                    {contact?.programs?.map((program: any) => (
                      <Link
                        to={`/programs/${PROGRAM_SLUGS[program.name]}`}
                        key={`mobile-dropdown-${PROGRAM_SLUGS[program.name]}`}
                      >
                        <div className="group relative p-0.5">
                          <span className="relative z-10 text-xl font-medium text-green-50 duration-300 ease-in-out group-hover:text-white">
                            {program.name}
                          </span>
                          <span className="absolute -left-1 -right-1 bottom-0 h-1.5 origin-bottom scale-x-0 transform rounded-lg bg-yellow-400 duration-300 ease-in-out group-hover:scale-x-100" />
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            </Popover.Panel>
          </Transition>
        </Popover>
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6">
      <nav className="mx-auto flex max-w-screen-xl items-center pt-5">
        <div className="flex w-full items-center justify-between">
          <div className="hidden items-center justify-between md:space-x-6 lg:flex lg:space-x-10">
            {navigation.map((link) => (
              <Fragment key={`desktop-link-${link.label}`}>
                {link.label === 'Хөтөлбөрүүд' ? (
                  <Menu as="div" className="relative">
                    {({ open }) => (
                      <>
                        <Menu.Button className="outline-none focus:outline-none">
                          <div className="group relative p-0.5">
                            <span
                              className={clsx(
                                'relative z-10 flex items-center text-lg font-medium duration-300 ease-in-out group-hover:text-green-600',
                                open ? 'text-green-600' : 'text-green-700',
                              )}
                            >
                              Хөтөлбөрүүд
                              <Icon
                                icon="chevronDown"
                                className={clsx(
                                  'h-4.5 ml-1.5 w-4.5 transform duration-300 ease-in-out',
                                  open && 'rotate-180',
                                )}
                                stroke={2}
                              />
                            </span>
                            <span className="absolute -left-1 -right-1 bottom-0 h-1.5 origin-bottom scale-x-0 transform rounded-lg bg-yellow-400 duration-300 ease-in-out group-hover:scale-x-100" />
                          </div>
                        </Menu.Button>
                        <Transition
                          as={Fragment}
                          enter="transition ease-out duration-300"
                          enterFrom="transform opacity-0 scale-95"
                          enterTo="transform opacity-100 scale-100"
                          leave="transition ease-in duration-200"
                          leaveFrom="transform opacity-100 scale-100"
                          leaveTo="transform opacity-0 scale-95"
                        >
                          <Menu.Items className="absolute left-1/2 z-20 mt-3 w-screen max-w-xs -translate-x-1/2 rounded-2xl border border-gray-50 bg-white p-4 shadow-lg outline-none focus:outline-none">
                            {contact?.programs?.map((program: any, index: number) => (
                              <Menu.Item
                                key={`desktop-dropdown-link-${PROGRAM_SLUGS[program.name]}`}
                                as="div"
                              >
                                {({ close }) => (
                                  <>
                                    <Link
                                      to={`/programs/${PROGRAM_SLUGS[program.name]}`}
                                      className={clsx(
                                        'group block w-full rounded-xl py-4 sm:p-5',
                                        location.pathname ===
                                          `/programs/${PROGRAM_SLUGS[program.name]}`
                                          ? 'bg-green-25'
                                          : 'transition duration-200 ease-in-out hover:bg-green-25/60',
                                      )}
                                      onClick={close}
                                    >
                                      <h5 className="text-lg font-semibold text-green-600">
                                        {program.name}
                                      </h5>
                                      {program.menu_desc && (
                                        <p className="mt-1 text-sm text-green-800 opacity-90">
                                          {program.menu_desc}
                                        </p>
                                      )}
                                    </Link>
                                    {contact?.programs.length - 1 !== index && (
                                      <hr className="my-1 border-green-200/30 sm:my-2" />
                                    )}
                                  </>
                                )}
                              </Menu.Item>
                            ))}
                          </Menu.Items>
                        </Transition>
                      </>
                    )}
                  </Menu>
                ) : (
                  link.href && (
                    <Link to={link.href}>
                      <div className="group relative p-0.5">
                        <span
                          className={clsx(
                            'relative z-10 text-lg font-medium',
                            isActive(link.href)
                              ? 'text-green-600'
                              : 'text-green-700 duration-300 ease-in-out group-hover:text-green-600',
                          )}
                        >
                          {link.label}
                        </span>
                        <span
                          className={clsx(
                            'absolute -left-1 -right-1 bottom-0 h-1.5 origin-bottom scale-x-0 transform rounded-lg bg-yellow-400',
                            isActive(link.href)
                              ? 'scale-x-100'
                              : 'duration-300 ease-in-out group-hover:scale-x-100',
                          )}
                        />
                      </div>
                    </Link>
                  )
                )}
              </Fragment>
            ))}
          </div>
          <div className="hidden lg:block">
            <Button onClick={() => handleFormClick(contact.registration)}>Бүртгүүлэх</Button>
          </div>
          <div className="block w-48 flex-shrink-0 flex-grow-0 sm:w-52 lg:hidden">
            <Link to="/">
              <img src={'/images/logo.png'} alt="Шинэ Өнөөдөр Сургууль" className="h-auto" />
            </Link>
          </div>
          <MobileNav />
        </div>
      </nav>
    </div>
  );
}
